import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { seoContentMap } from "../lib/seoContent";

export default function SeoContentSection({ category }) {
  const content = seoContentMap[category];
  const [openFaq, setOpenFaq] = useState(null);

  // Dynamically update Meta Data & FAQ Schema when SEO content is loaded
  useEffect(() => {
    if (content) {
      const originalTitle = document.title;
      
      // Update Page Title
      if (content.metaTitle) {
        document.title = content.metaTitle;
      }
      
      // Update Meta Description
      let metaDescription = document.querySelector('meta[name="description"]');
      let originalDescription = metaDescription ? metaDescription.getAttribute("content") : "";
      
      if (content.metaDescription) {
        if (!metaDescription) {
          metaDescription = document.createElement("meta");
          metaDescription.name = "description";
          document.head.appendChild(metaDescription);
        }
        metaDescription.setAttribute("content", content.metaDescription);
      }

      // Inject JSON-LD FAQ Schema
      let faqScript = document.getElementById(`faq-schema-${category}`);
      if (content.faqSchema) {
        if (!faqScript) {
          faqScript = document.createElement("script");
          faqScript.type = "application/ld+json";
          faqScript.id = `faq-schema-${category}`;
          document.head.appendChild(faqScript);
        }
        faqScript.textContent = JSON.stringify(content.faqSchema);
      }

      // Cleanup to restore original values on unmount
      return () => {
        document.title = originalTitle;
        if (metaDescription && originalDescription) {
          metaDescription.setAttribute("content", originalDescription);
        } else if (metaDescription) {
          metaDescription.remove();
        }
        if (faqScript) {
          faqScript.remove();
        }
      };
    }
  }, [category, content]);

  if (!content) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="border-t border-sand-300 bg-sand-50/50 py-16 mt-16"
      data-testid={`seo-section-${category}`}
    >
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center md:text-left mb-10 border-b border-sand-200 pb-8">
          {content.subtitle && (
            <h2 className="font-serif text-3xl md:text-5xl text-forest-900 leading-tight mb-4">
              {content.subtitle}
            </h2>
          )}
        </div>

        {/* Introduction Paragraphs */}
        <div className="space-y-6 text-forest-500 font-sans leading-relaxed text-base md:text-lg mb-12">
          {content.paragraphs.map((p, idx) => (
            <p key={idx}>{p}</p>
          ))}
        </div>

        {/* Subsections */}
        {content.sections && content.sections.length > 0 && (
          <div className="space-y-12 mb-16">
            {content.sections.map((sec, idx) => (
              <div key={idx} className="border-l-2 border-sage/30 pl-6 space-y-4">
                <h3 className="font-serif text-2xl md:text-3xl text-forest-900 leading-tight font-medium">
                  {sec.title}
                </h3>
                
                <div className="space-y-4 text-forest-500 font-sans leading-relaxed text-base">
                  {sec.paragraphs && sec.paragraphs.map((p, pIdx) => (
                    <p key={pIdx}>{p}</p>
                  ))}
                </div>

                {sec.bullets && sec.bullets.length > 0 && (
                  <ul className="grid sm:grid-cols-2 gap-3 text-forest-500 font-sans text-sm md:text-base py-2">
                    {sec.bullets.map((bullet, bIdx) => (
                      <li key={bIdx} className="flex items-start gap-2.5">
                        <span className="text-sage mt-1 flex-shrink-0">
                          <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                            <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm3.707 7.707l-4 4a1 1 0 01-1.414 0l-2-2a1 1 0 111.414-1.414L9 11.586l3.293-3.293a1 1 0 011.414 1.414z" />
                          </svg>
                        </span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {sec.outro && (
                  <p className="text-forest-500 font-sans leading-relaxed text-base pt-2 italic">
                    {sec.outro}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* FAQs Accordion */}
        {content.faqs && content.faqs.length > 0 && (
          <div className="border-t border-sand-300 pt-12">
            <h3 className="font-serif text-3xl text-forest-900 mb-8 text-center md:text-left">
              Frequently Asked Questions
            </h3>
            <div className="space-y-4">
              {content.faqs.map((faq, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div key={idx} className="border border-sand-200 bg-white rounded-md overflow-hidden transition-all duration-300 hover:border-sage/40">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left font-sans font-medium text-forest-900 hover:text-sage transition-colors focus:outline-none"
                    >
                      <span className="pr-4">{faq.question}</span>
                      <svg
                        className={`w-5 h-5 flex-shrink-0 transform transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: "easeInOut" }}
                        >
                          <div className="p-5 pt-0 text-forest-500 text-sm md:text-base leading-relaxed border-t border-sand-100 bg-sand-50/30">
                            {faq.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </motion.section>
  );
}
